// GPU sprite batch for entities (player, blitted objects).

import { Container, Sprite, Texture, Rectangle } from 'pixi.js';
import type { Camera } from '../camera';
import { getImg, getAssetScale } from '../assets';
import type { Surface } from '../surface';
import { surfaceToIndexTexture } from './index-texture';
import { worldToScreen } from '../camera';
import { profilerMeasure } from '../../dev/profiler';
import { BASE_PLAYERW, BASE_PLAYERH, RES_SCALE } from '../../core/constants';

const tr = Math.trunc;

const layer = new Container();
const texCache = new Map<string, Texture>();

export function getSpriteLayer(): Container {
  return layer;
}

function frameTex(
  surf: Surface,
  cacheKey: string,
  sx: number,
  sy: number,
  sw: number,
  sh: number
): Texture {
  const key = `${cacheKey}:${sx},${sy},${sw},${sh}`;
  let t = texCache.get(key);
  if (!t) {
    const base = surfaceToIndexTexture(surf);
    t = new Texture({
      source: base.source,
      frame: new Rectangle(sx, sy, sw, sh),
    });
    texCache.set(key, t);
  }
  return t;
}

export function clearSpriteLayer(): void {
  layer.removeChildren();
}

export function drawIndexedSpriteDirect(
  datPath: string,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
  surfOverride?: Surface,
  texOverride?: Texture
): void {
  const surf = surfOverride ?? getImg(datPath);
  const key = datPath || `surf:${surf.w}x${surf.h}`;
  const spr = new Sprite(texOverride ?? frameTex(surf, key, sx, sy, sw, sh));
  spr.roundPixels = true;
  spr.position.set(Math.round(dx), Math.round(dy));
  spr.width = dw;
  spr.height = dh;
  layer.addChild(spr);
}

export function drawIndexedSpriteWorld(
  datPath: string,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  wx: number,
  wy: number,
  dw: number,
  dh: number,
  cam: Camera
): void {
  const s = worldToScreen(wx, wy, cam);
  drawIndexedSpriteDirect(datPath, sx, sy, sw, sh, s.x, s.y, dw, dh);
}

export function drawPlayerGpu(cam: Camera, x: number, y: number, plDir: number, plFrm: number): void {
  profilerMeasure('gpu.player', () => {
    const pScale = getAssetScale('dat/i/player.png');
    const fw = BASE_PLAYERW * pScale;
    const fh = BASE_PLAYERH * pScale;
    const dw = (fw / pScale) * RES_SCALE;
    const dh = (fh / pScale) * RES_SCALE;
    drawIndexedSpriteDirect('dat/i/player.png', plFrm * fw, plDir * fh, fw, fh, x, y, dw, dh);
  });
}

export function drawWorldBlitGpu(
  datPath: string,
  wx: number,
  wy: number,
  cam: Camera,
  nativeScale?: number
): void {
  const ns = nativeScale ?? getAssetScale(datPath);
  const surf = getImg(datPath);
  const dw = (surf.w / ns) * RES_SCALE;
  const dh = (surf.h / ns) * RES_SCALE;
  const s = worldToScreen(wx, wy, cam);
  drawIndexedSpriteDirect(datPath, 0, 0, surf.w, surf.h, s.x, s.y, dw, dh);
}
